<!--
  /settings — user-facing configuration panel.

  Bound to the reactive `settings` store in `lib/settings/settings.svelte.ts`;
  every field writes back via `updateSettings`, which both patches the
  reactive state and persists to Dexie. Opening `/settings`, changing a
  field, and returning to Record or TestVideo must reflect the change
  immediately — we intentionally re-read from the store on every mount
  through `initSettings()`.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import {
    getSettings,
    initSettings,
    updateSettings,
    type DetectionFps,
    type CalibrationIntervalS,
    type Handedness,
  } from '../lib/settings/settings.svelte';

  let ready = $state(false);
  onMount(async () => {
    await initSettings();
    ready = true;
  });

  const s = $derived(getSettings());

  function patchContactThreshold(v: number) {
    void updateSettings({ contactThresholdSpeed: clamp(v, 0.4, 2.0) });
  }
  function patchKSigma(v: number) {
    void updateSettings({ contactKSigma: clamp(v, 1.0, 3.0) });
  }
  function patchRefractory(v: number) {
    void updateSettings({ contactRefractoryMs: clamp(v, 300, 1000) });
  }
  function patchSceneThreshold(v: number) {
    void updateSettings({ sceneChangeThreshold: clamp(v, 10, 80) });
  }
  function patchDetectionFps(v: DetectionFps) {
    void updateSettings({ detectionFps: v });
  }
  function patchCalibInterval(v: CalibrationIntervalS) {
    void updateSettings({ recalibrateEverySec: v });
  }
  function patchHandedness(v: Handedness) {
    void updateSettings({ handedness: v });
  }
  function patchOverlay(key: keyof typeof s.overlays, v: boolean) {
    void updateSettings({ overlays: { ...s.overlays, [key]: v } });
  }
  function patchPauseOnCut(v: boolean) {
    void updateSettings({ pauseOnCut: v });
  }

  function clamp(v: number, lo: number, hi: number) {
    if (Number.isNaN(v)) return lo;
    return Math.max(lo, Math.min(hi, v));
  }
</script>

<section class="wrap">
  <header class="hdr">
    <a href="#/" class="back">‹ Voltar</a>
    <h1>Configurações</h1>
    <span></span>
  </header>

  {#if !ready}
    <p class="note">Carregando…</p>
  {:else}
    <fieldset class="card">
      <legend>Detecção de contato</legend>
      <label>
        <span>Threshold absoluto <em>(0.4 – 2.0)</em></span>
        <input
          type="range"
          min="0.4"
          max="2.0"
          step="0.05"
          value={s.contactThresholdSpeed}
          oninput={(e) => patchContactThreshold(+(e.currentTarget as HTMLInputElement).value)}
        />
        <output>{s.contactThresholdSpeed.toFixed(2)}</output>
      </label>
      <label>
        <span>kSigma adaptativo <em>(1.0 – 3.0)</em></span>
        <input
          type="range"
          min="1.0"
          max="3.0"
          step="0.1"
          value={s.contactKSigma}
          oninput={(e) => patchKSigma(+(e.currentTarget as HTMLInputElement).value)}
        />
        <output>{s.contactKSigma.toFixed(1)}</output>
      </label>
      <label>
        <span>Período refratário (ms) <em>(300 – 1000)</em></span>
        <input
          type="range"
          min="300"
          max="1000"
          step="50"
          value={s.contactRefractoryMs}
          oninput={(e) => patchRefractory(+(e.currentTarget as HTMLInputElement).value)}
        />
        <output>{s.contactRefractoryMs} ms</output>
      </label>
    </fieldset>

    <fieldset class="card">
      <legend>Overlays</legend>
      <label class="check">
        <input
          type="checkbox"
          checked={s.overlays.stickman}
          onchange={(e) => patchOverlay('stickman', (e.currentTarget as HTMLInputElement).checked)}
        />
        Stickman
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={s.overlays.miniCourt}
          onchange={(e) => patchOverlay('miniCourt', (e.currentTarget as HTMLInputElement).checked)}
        />
        Mini quadra
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={s.overlays.ball}
          onchange={(e) => patchOverlay('ball', (e.currentTarget as HTMLInputElement).checked)}
        />
        Bolinha
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={s.overlays.courtPolygon}
          onchange={(e) => patchOverlay('courtPolygon', (e.currentTarget as HTMLInputElement).checked)}
        />
        Polígono da quadra
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={s.overlays.ballTrail}
          onchange={(e) => patchOverlay('ballTrail', (e.currentTarget as HTMLInputElement).checked)}
        />
        Trail da bolinha
      </label>
    </fieldset>

    <fieldset class="card">
      <legend>Desempenho</legend>
      <label>
        <span>FPS de detecção</span>
        <div class="seg">
          {#each [7, 15, 30] as fps}
            <button
              type="button"
              class:active={s.detectionFps === fps}
              onclick={() => patchDetectionFps(fps as DetectionFps)}
            >
              {fps} fps
            </button>
          {/each}
        </div>
      </label>
      <label>
        <span>Intervalo de re-calibração</span>
        <div class="seg">
          {#each [2, 4, 8] as sec}
            <button
              type="button"
              class:active={s.recalibrateEverySec === sec}
              onclick={() => patchCalibInterval(sec as CalibrationIntervalS)}
            >
              {sec} s
            </button>
          {/each}
        </div>
      </label>
    </fieldset>

    <fieldset class="card">
      <legend>Mão dominante</legend>
      <div class="seg">
        <button type="button" class:active={s.handedness === 'right'} onclick={() => patchHandedness('right')}>
          Destro
        </button>
        <button type="button" class:active={s.handedness === 'left'} onclick={() => patchHandedness('left')}>
          Canhoto
        </button>
      </div>
    </fieldset>

    <fieldset class="card">
      <legend>Corte de câmera</legend>
      <label>
        <span>Limiar <em>(10 – 80)</em></span>
        <input
          type="range"
          min="10"
          max="80"
          step="1"
          value={s.sceneChangeThreshold}
          oninput={(e) => patchSceneThreshold(+(e.currentTarget as HTMLInputElement).value)}
        />
        <output>{s.sceneChangeThreshold}</output>
      </label>
      <label class="check">
        <input
          type="checkbox"
          checked={s.pauseOnCut}
          onchange={(e) => patchPauseOnCut((e.currentTarget as HTMLInputElement).checked)}
        />
        Pausar análise em cortes
      </label>
    </fieldset>
  {/if}
</section>

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
    max-width: 720px;
    margin: 0 auto;
    color: #e2e8f0;
  }
  .hdr { display: flex; align-items: center; justify-content: space-between; }
  .hdr h1 { font-size: 20px; font-weight: 700; margin: 0; }
  .back {
    background: #1e293b;
    padding: 6px 12px;
    border-radius: 6px;
    color: #e2e8f0;
    text-decoration: none;
    font-size: 14px;
  }
  .card {
    background: #0f172a;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 12px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  legend { padding: 0 6px; font-size: 13px; color: #94a3b8; letter-spacing: 0.05em; text-transform: uppercase; }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
  }
  label > span { color: #cbd5e1; }
  label em { color: #64748b; font-style: normal; font-size: 12px; margin-left: 6px; }
  label output { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #facc15; }
  label.check { flex-direction: row; align-items: center; gap: 8px; }
  .seg { display: flex; gap: 6px; }
  .seg button {
    flex: 1;
    background: #1e293b;
    border: 1px solid rgba(148, 163, 184, 0.25);
    color: #cbd5e1;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    cursor: pointer;
  }
  .seg button.active { background: #22c55e; color: #0f172a; border-color: #22c55e; font-weight: 700; }
  input[type='range'] { accent-color: #22c55e; }
  .note { color: #94a3b8; padding: 10px; }
</style>
